import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency } from "@/lib/format";
import { ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface BlockEarningsCardProps {
  basePay: number;
  tip: number;
  noRoutePayout: number;
  hourlyRate: number;
  actualHourlyRate?: number | null;
  perMileRate?: number | null;
  fuelEstimate?: {
    cost: number;
    mpg: number;
    fuelPrice: number;
  } | null;
}

export function BlockEarningsCard({
  basePay,
  tip,
  noRoutePayout,
  hourlyRate,
  actualHourlyRate,
  perMileRate,
  fuelEstimate,
}: BlockEarningsCardProps) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const totalEarnings = basePay + tip + noRoutePayout;
  const hasFuel = fuelEstimate && fuelEstimate.cost > 0;
  const netProfit = hasFuel ? totalEarnings - fuelEstimate.cost : totalEarnings;
  const displayRate = actualHourlyRate ?? hourlyRate;

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        {/* Hero: Gross -> Net or just Total */}
        {hasFuel ? (
          <div className="flex items-center justify-center gap-3 py-1">
            <div className="text-center">
              <p className="text-2xl font-bold">{formatCurrency(totalEarnings)}</p>
              <p className="text-xs text-muted-foreground">gross</p>
            </div>
            <div className="flex flex-col items-center">
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-destructive">-{formatCurrency(fuelEstimate.cost)}</p>
            </div>
            <div className="text-center">
              <p className={cn(
                "text-2xl font-bold",
                netProfit >= 0 ? "text-green-600" : "text-destructive"
              )}>
                {formatCurrency(netProfit)}
              </p>
              <p className="text-xs text-muted-foreground">net</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-1">
            <p className="text-3xl font-bold">{formatCurrency(totalEarnings)}</p>
          </div>
        )}

        {/* Rates row */}
        <div className="flex justify-center gap-4 text-sm">
          <span className="font-medium">
            {formatCurrency(Math.round(displayRate))}/hr
            {actualHourlyRate && (
              <span className="text-xs text-muted-foreground ml-1">actual</span>
            )}
          </span>
          {perMileRate && (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="font-medium">
                {formatCurrency(Math.round(perMileRate * 100) / 100)}/mi
              </span>
            </>
          )}
        </div>

        {/* Fuel disclaimer */}
        {hasFuel && (
          <p className="text-xs text-muted-foreground text-center">
            Est. fuel: {fuelEstimate.mpg} MPG @ {formatCurrency(fuelEstimate.fuelPrice)}/gal
          </p>
        )}

        {/* Collapsible breakdown */}
        <button
          onClick={() => setShowBreakdown(!showBreakdown)}
          className="flex items-center justify-center gap-1 w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
        >
          {showBreakdown ? (
            <>
              <ChevronUp className="h-4 w-4" />
              Hide breakdown
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              View breakdown
            </>
          )}
        </button>

        {showBreakdown && (
          <div className="space-y-2 pt-2 border-t">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Base Pay</span>
              <span>{formatCurrency(basePay)}</span>
            </div>
            {tip > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tips</span>
                <span>{formatCurrency(tip)}</span>
              </div>
            )}
            {noRoutePayout > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Adjustments</span>
                <span>{formatCurrency(noRoutePayout)}</span>
              </div>
            )}
            {actualHourlyRate && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Booked Rate</span>
                <span className="text-muted-foreground">
                  {formatCurrency(Math.round(hourlyRate))}/hr
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
